import { useState, useRef, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { checkImageQuality } from "@/lib/imageQuality";
import { getTracking } from "@/lib/tracking";
import {
  Loader2, CheckCircle2, Upload, X, ChevronLeft, ChevronRight,
  Car, User, ShieldCheck, IdCard, UserPlus, Check, CreditCard, Ban, Info, Clock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useBooking } from "@/context/BookingContext";
import { LivePricingHint } from "@/components/LivePricingHint";

import { useVehicles, vehicleName } from "@/lib/useVehicles";
import { fmtDateTime } from "@/lib/datetime";
import { PricingBreakdown } from "@/components/PricingBreakdown";
import { usePublicPricing, packageBase } from "@/lib/usePublicPricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const STEPS = [
  { id: "vehicle", label: "Vehicle", icon: Car },
  { id: "driver", label: "Driver", icon: User },
  { id: "insurance", label: "Insurance", icon: ShieldCheck },
  { id: "verify", label: "ID Verify", icon: IdCard },
  { id: "addons", label: "Add-ons", icon: UserPlus },
  { id: "review", label: "Review", icon: Check },
];

const emptyForm = {
  vehicle: "", coverage_selection: "own", coverage_ack: false, pickup_date: "", return_date: "",
  pickup_time: "10:00", return_time: "10:00",
  name: "", email: "", phone: "", date_of_birth: "", address: "",
  dl_number: "", dl_state: "", dl_expiration: "",
  insurance_attested: false, insurance_bring_later: false, add_extra_driver: false, extra_driver_name: "",
  add_damage_protection: false, agreed: false, referral_code: "",
};

const COVERAGE_SHORT = {
  own: "I intend to rely on my own insurance coverage.",
  rental: "Optional third-party rental insurance offered through an independent provider.",
  decline: "Decline the optional rental insurance.",
};
const COVERAGE_INFO = {
  own: "You intend to rely on your own automobile insurance. It is your responsibility to determine whether your policy applies to this rental transaction. AutoHire does not guarantee that your personal insurance provides coverage, and any verification AutoHire performs is not a guarantee that a claim will be covered.",
  rental: "Optional third-party rental insurance is issued/provided by an independent insurance provider and is subject to that provider's terms, limits, conditions, and exclusions. AutoHire of St. Louis LLC is NOT the insurance company.",
  decline: "You voluntarily decline optional rental insurance offered through AutoHire. You acknowledge you may not have personal automobile liability insurance applicable to the rental vehicle, and you may be personally responsible for liabilities, losses, damages, claims, and expenses not paid by applicable insurance, subject to applicable law. This selection does NOT alter AutoHire's separate obligation to maintain any financial responsibility required by law — and it does NOT require you to purchase the optional Damage Waiver.",
};
const DECLINE_NOTICE = "Declining optional rental insurance does NOT require you to purchase the Damage Waiver. The Damage Waiver remains a separate, optional $15/day selection on the next step. It is NOT insurance and does not provide liability coverage.";
const MO_NOTICE = "COLLISION DAMAGE WAIVER AND CAR RENTAL INSURANCE NOTICE: OUR CONTRACT OFFERS FOR AN ADDITIONAL CHARGE COLLISION DAMAGE WAIVER AND CAR RENTAL INSURANCE PRODUCTS. BEFORE DECIDING WHETHER TO PURCHASE ANY OF THESE OPTIONAL PRODUCTS, YOU MAY WISH TO DETERMINE WHETHER YOUR PERSONAL INSURANCE OR CREDIT CARD PROVIDES YOU COVERAGE DURING THE RENTAL PERIOD. THE PURCHASE OF ANY OF THESE OPTIONAL PRODUCTS IS NOT REQUIRED TO RENT A VEHICLE.";
const COVERAGE_TEXT = {
  own: "I intend to rely on my own automobile insurance. I understand it is my responsibility to determine whether my policy applies to this rental transaction. AutoHire does not guarantee that my personal insurance provides coverage, and if AutoHire collects or verifies proof of insurance, that verification is not a guarantee that a particular claim will be covered.",
  rental: "I voluntarily elect to purchase optional third-party rental insurance. I understand AutoHire of St. Louis LLC is NOT the insurance company; coverage is issued/provided by an independent third-party insurance provider and is subject to that provider's terms, limits, conditions, and exclusions.",
  decline: "I voluntarily decline optional rental insurance offered through AutoHire. I acknowledge I may not have personal automobile liability insurance applicable to the rental vehicle. I understand I may be personally responsible for liabilities, losses, damages, claims, and expenses that are not paid by applicable insurance, subject to applicable law. I understand this selection does not alter AutoHire's separate obligation to maintain any financial responsibility required by applicable law.",
};
const DW_LANGUAGE = "The Damage Waiver / Damage Protection is a contractual protection offered by AutoHire and is NOT insurance. If accepted and paid for, it may reduce your financial responsibility for covered accidental damage to the rental vehicle only, subject to the Damage Waiver Addendum, exclusions, and all Rental Agreement terms. It does not provide liability insurance, does not cover injuries, does not cover damage to other vehicles or property, and does not replace required insurance coverage.";

const inputCls =
  "w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2.5 text-white placeholder:text-white/30 rounded-none transition-colors";

function FileUpload({ label, value, onUploaded, testid }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    checkImageQuality(file).then((q) => q.warnings.forEach((w) => toast.warning(w, { duration: 6000 })));
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.path);
    } catch {
      toast.error("Upload failed. Try a smaller image.");
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/45">{label}</label>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} data-testid={testid} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className={`mt-2 w-full h-32 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-colors overflow-hidden relative ${
          value ? "border-[#F2B705]/50 bg-[#F2B705]/[0.05]" : "border-white/15 hover:border-[#F2B705]/50"
        }`}
      >
        {preview ? (
          <img src={preview} alt={label} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <>
            <Upload size={20} className="text-[#F2B705]" />
            <span className="text-white/50 text-sm">Tap to upload or take a photo</span>
          </>
        )}
        {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="animate-spin text-[#F2B705]" /></div>}
        {value && !uploading && <div className="absolute top-2 right-2 bg-[#F2B705] rounded-full p-1"><Check size={14} className="text-black" /></div>}
      </button>
    </div>
  );
}

export const BookingDialog = () => {
  const { open, setOpen, vehicle, prefill, reservationToken } = useBooking();
  const { vehicles } = useVehicles();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState({});
  const [learnMore, setLearnMore] = useState(null);
  const [ackError, setAckError] = useState(false);
  const ackRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [savingCard, setSavingCard] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFile = (k, v) => setFiles((f) => ({ ...f, [k]: v }));

  const selectedVehicle = form.vehicle || vehicle;
  const car = useMemo(() => vehicles.find((c) => vehicleName(c) === selectedVehicle), [selectedVehicle, vehicles]);
  const premium = car?.premium?.enabled ? car.premium : null;
  const premiumOpts = premium ? [
    { days: parseInt(premium.exact_days) || 3, price: premium.package_price || 499, label: `${parseInt(premium.exact_days) || 3}-Day Package` },
    ...(premium.packages || []).filter((p) => p.days && p.price).map((p) => ({ days: parseInt(p.days), price: p.price, label: p.label || `${p.days}-Day Package` })),
  ] : [];
  const [premDays, setPremDays] = useState(0);
  const premFlexible = !!premium && Number(premium.extra_day_rate) > 0;
  const premiumDays = premium ? (premiumOpts.some((o) => o.days === premDays) ? premDays : premiumOpts[0].days) : 0;

  useEffect(() => {
    if (premium && !premFlexible && form.pickup_date) {
      const d = new Date(`${form.pickup_date}T12:00:00`);
      d.setDate(d.getDate() + premiumDays);
      const lock = d.toISOString().slice(0, 10);
      setForm((p) => (p.return_date === lock ? p : { ...p, return_date: lock }));
    }
  }, [premium, premFlexible, premiumDays, form.pickup_date]);
  const pubPricing = usePublicPricing();
  const tpi = pubPricing.third_party_insurance || {};

  const [bookedRanges, setBookedRanges] = useState([]);
  const [leadCfg, setLeadCfg] = useState({ lead_enabled: false, lead_hours: 4 });
  useEffect(() => {
    if (!open) return;
    if (prefill) setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(prefill).filter(([, v]) => v)) }));
    axios.get(`${API}/booking-config`).then(({ data }) => setLeadCfg(data)).catch(() => {});
  }, [open, prefill]);
  useEffect(() => {
    if (!open || !selectedVehicle) { setBookedRanges([]); return; }
    axios.get(`${API}/availability`, { params: { vehicle: selectedVehicle } })
      .then(({ data }) => setBookedRanges(data.ranges || []))
      .catch(() => setBookedRanges([]));
  }, [open, selectedVehicle]);

  // Earliest allowed pickup = now + prep window (Central-aware via display helper)
  const earliestPickup = useMemo(() => {
    if (!leadCfg.lead_enabled) return null;
    return new Date(Date.now() + (leadCfg.lead_hours || 0) * 3600000);
  }, [leadCfg]);

  const leadError = useMemo(() => {
    if (!earliestPickup || !form.pickup_date) return null;
    const picked = new Date(`${form.pickup_date}T${form.pickup_time || "00:00"}`);
    if (isNaN(picked.getTime())) return null;
    if (picked < earliestPickup) {
      return `We need a ${leadCfg.lead_hours}-hour prep window. Earliest pickup: ${fmtDateTime(earliestPickup)}.`;
    }
    return null;
  }, [earliestPickup, form.pickup_date, form.pickup_time, leadCfg.lead_hours]);

  const rangeConflict = useMemo(() => {
    if (!form.pickup_date || !form.return_date) return null;
    return bookedRanges.find((r) => form.pickup_date <= r.end && r.start <= form.return_date) || null;
  }, [form.pickup_date, form.return_date, bookedRanges]);

  const days = useMemo(() => {
    if (!form.pickup_date || !form.return_date) return 1;
    const d = Math.round((new Date(`${form.return_date}T00:00`) - new Date(`${form.pickup_date}T00:00`)) / 86400000);
    return d > 0 ? d : 1;
  }, [form.pickup_date, form.return_date]);

  const age = useMemo(() => {
    if (!form.date_of_birth) return null;
    const dob = new Date(form.date_of_birth);
    const diff = Date.now() - dob.getTime();
    return Math.floor(diff / (365.25 * 86400000));
  }, [form.date_of_birth]);

  const isYoung = age !== null && age >= 21 && age <= 24;
  const insuranceType = form.coverage_selection === "rental" ? "AllIn" : "BYOI";
  const waiverOn = form.add_damage_protection;
  const dailyRate = pubPricing.additional_day_rate;
  const extra = form.add_extra_driver ? pubPricing.additional_driver_day : 0;
  const damage = waiverOn ? pubPricing.damage_waiver_day : 0;
  const young = isYoung ? pubPricing.young_driver_day : 0;
  const coverageDaily = insuranceType === "AllIn" ? pubPricing.allin_coverage_day : 0;
  // Company-wide package base: 3-Day Special + $/day, Weekly Special for 7+ days.
  const vehicleBase = packageBase(pubPricing, days);
  const addOnsTotal = (extra + damage + young + coverageDaily) * days;
  const clientTotal = vehicleBase + addOnsTotal;

  // Server-authoritative itemized quote (Pricing Engine)
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    if (!open || !car || !form.pickup_date || !form.return_date) { setQuote(null); return; }
    const t = setTimeout(() => {
      axios.post(`${API}/pricing/quote`, {
        vehicle: selectedVehicle, days,
        coverage_selection: form.coverage_selection || (insuranceType === "AllIn" ? "rental" : "own"),
        add_damage_protection: waiverOn, add_extra_driver: form.add_extra_driver, young_driver: isYoung,
      }).then(({ data }) => setQuote(data.vehicle_matched ? data : null)).catch(() => setQuote(null));
    }, 300);
    return () => clearTimeout(t);
  }, [open, car, selectedVehicle, days, form.pickup_date, form.return_date, form.coverage_selection,
      waiverOn, form.add_extra_driver, isYoung, insuranceType]);

  const total = quote ? quote.total : clientTotal;

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const validateStep = () => {
    switch (STEPS[step].id) {
      case "vehicle":
        if (!car) return "Please select a vehicle.";
        if (!form.pickup_date || !form.return_date) return "Please choose pickup and return dates.";
        if (new Date(form.return_date) <= new Date(form.pickup_date)) return "Return date must be after pickup.";
        if (premium && !premFlexible && days !== premiumDays) return `This vehicle rents in fixed packages (${premiumOpts.map((o) => `${o.days} days`).join(" or ")}) — the return date is set automatically from your pickup date and chosen package.`;
        if (days < 3) return "Minimum rental is 3 calendar days.";
        if (rangeConflict) return `This car is already booked ${fmtDate(rangeConflict.start)} – ${fmtDate(rangeConflict.end)}. Please choose different dates.`;
        if (leadError) return leadError;
        return null;
      case "driver":
        if (!form.name || !form.email || !form.phone || !form.date_of_birth) return "Please complete all driver fields.";
        if (age !== null && age < 21) return "Drivers must be at least 21 years old.";
        return null;
      case "insurance":
        if (!form.coverage_ack) return "Please read and check the coverage certification to continue.";
        if (form.coverage_selection === "own" && !form.insurance_bring_later && (!files.insurance_card || !form.insurance_attested))
          return "Please upload your insurance card and confirm it meets requirements — or check the box below if you'll bring it at pickup.";
        return null;
      case "verify":
        if (!files.license_front || !files.license_back || !files.selfie)
          return "Please upload license front, back, and a selfie.";
        if (!form.dl_number || !form.dl_state || !form.dl_expiration)
          return "Please enter your driver's license number, state, and expiration.";
        return null;
      case "addons":
        if (form.add_extra_driver && (!form.extra_driver_name || !files.extra_driver_license))
          return "Please add the extra driver's name and license photo.";
        return null;
      default:
        return null;
    }
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) {
      toast.error(err);
      if (STEPS[step].id === "insurance" && !form.coverage_ack) {
        setAckError(true);
        ackRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    if (STEPS[step].id === "driver" && form.email) {
      axios.post(`${API}/leads/abandoned`, {
        name: form.name, email: form.email, phone: form.phone,
        vehicle: selectedVehicle || "", lead_source: getTracking().lead_source,
      }).catch(() => {});
    }
    goNext();
  };

  const submit = async () => {
    if (!form.agreed) { toast.error("Please agree to the rental terms to submit."); return; }
    setLoading(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone,
        vehicle: selectedVehicle, pickup_date: form.pickup_date, return_date: form.return_date,
        pickup_time: form.pickup_time, return_time: form.return_time,
        insurance_type: insuranceType, coverage_selection: form.coverage_selection, coverage_ack: form.coverage_ack,
        date_of_birth: form.date_of_birth, address: form.address,
        dl_number: form.dl_number, dl_state: form.dl_state, dl_expiration: form.dl_expiration,
        days, daily_rate: dailyRate, add_extra_driver: form.add_extra_driver,
        extra_driver_name: form.extra_driver_name, add_damage_protection: waiverOn,
        young_driver_fee: isYoung, total_price: total,
        license_front_path: files.license_front, license_back_path: files.license_back,
        selfie_path: files.selfie, insurance_card_path: files.insurance_card || null,
        insurance_bring_later: form.insurance_bring_later,
        extra_driver_license_path: files.extra_driver_license || null,
        referral_code: form.referral_code || "",
        reservation_token: reservationToken || "",
        ...getTracking(),
        message: `Coverage: ${form.coverage_selection} · ${days} day(s)${waiverOn ? " · +Damage Waiver" : ""} · Total $${total}`,
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      setBookingId(data.id);
      setDone(true);
      toast.success("Booking submitted! We'll confirm within the hour.");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data?.detail || "Those dates are unavailable for this vehicle. Please pick different dates.");
        setStep(0);
      } else if (err.response?.status === 400) {
        toast.error(err.response.data?.detail || "Please review your booking details.");
        setStep(0);
      } else {
        toast.error("Something went wrong. Please call us instead.");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveCardOnFile = async () => {
    if (!bookingId) return;
    setSavingCard(true);
    try {
      const { data } = await axios.post(`${API}/payments/setup-card`, { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start secure card setup. You can do this later from your pay link.");
      setSavingCard(false);
    }
  };

  const close = (v) => {
    setOpen(v);
    if (!v) setTimeout(() => { setForm(emptyForm); setFiles({}); setStep(0); setDone(false); setBookingId(null); }, 250);
  };

  const StepIcon = STEPS[step].icon;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="booking-dialog" className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-[640px] max-h-[92vh] overflow-y-auto p-0">
        {done ? (
          <div className="py-14 px-8 text-center flex flex-col items-center gap-4" data-testid="booking-success">
            <CheckCircle2 className="text-[#F2B705]" size={56} />
            <h3 className="font-display text-2xl font-bold">Application Received</h3>
            <p className="text-white/60 max-w-sm">Thanks, {form.name.split(" ")[0]}! Our team will verify your details and confirm availability shortly. Drive Today. Pay Less. Stay Moving.</p>
            {quote && (
              <div className="w-full max-w-sm text-left">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1.5">Your Itemized Charges</p>
                <PricingBreakdown quote={quote} showUnselected={false} dueLabel="Booking Total" />
              </div>
            )}
            <div className="w-full max-w-sm rounded-xl border border-[#F2B705]/30 bg-[#F2B705]/[0.06] p-4 mt-2 text-left">
              <div className="flex items-center gap-2 text-[#F2B705] font-semibold text-sm"><CreditCard size={16} /> Save a card on file (optional)</div>
              <p className="text-white/55 text-xs mt-1.5">Securely store a card with Stripe so approved extras, tolls or damages can be handled fast — no charge now.</p>
              <button data-testid="booking-save-card-btn" onClick={saveCardOnFile} disabled={savingCard}
                className="mt-3 w-full bg-[#F2B705] text-black font-semibold px-4 py-2.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {savingCard ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />} Save Card Securely
              </button>
            </div>
            <button data-testid="booking-close-button" onClick={() => close(false)} className="mt-2 bg-white/10 text-white font-semibold px-6 py-2.5 rounded-md hover:bg-white/20 transition-colors">Done</button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6">
              <DialogTitle className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                <StepIcon className="text-[#F2B705]" size={22} /> {STEPS[step].label}
              </DialogTitle>
              <DialogDescription className="sr-only">Book a car — step {step + 1} of {STEPS.length}: {STEPS[step].label}</DialogDescription>
            </DialogHeader>

            {/* Stepper */}
            <div className="px-6 flex items-center gap-1.5 mt-1">
              {STEPS.map((s, i) => (
                <div key={s.id} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#F2B705]" : "bg-white/10"}`} />
              ))}
            </div>
            <p className="px-6 text-xs text-white/40 mt-2">Step {step + 1} of {STEPS.length}</p>

            <div className="mx-6 mt-3 flex items-center gap-2 rounded-md bg-[#F2B705]/[0.08] border border-[#F2B705]/30 px-3 py-2" data-testid="booking-policy-notice">
              <Ban size={15} className="text-[#F2B705] shrink-0" />
              <p className="text-xs text-[#F2B705]/90 font-medium"><span className="font-bold uppercase">No out-of-town trips</span> — all rentals must stay within the St. Louis metro area.</p>
            </div>

            <div className="px-6 py-5 space-y-5 min-h-[280px]">
              {/* STEP 1: Vehicle & Dates */}
              {STEPS[step].id === "vehicle" && (
                <>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/45">Vehicle</label>
                    <select data-testid="wizard-vehicle" className={`${inputCls} appearance-none`} value={selectedVehicle} onChange={(e) => set("vehicle", e.target.value)}>
                      <option value="" className="bg-[#0A0A0A]">Select a vehicle</option>
                      {vehicles.filter((c) => c.status === "available").map((c) => <option key={c.id} value={vehicleName(c)} className="bg-[#0A0A0A]">{c.year} {vehicleName(c)} — {c.premium?.enabled ? `PREMIUM ${[`${parseInt(c.premium.exact_days) || 3}d $${c.premium.package_price || 499}`, ...(c.premium.packages || []).filter((p) => p.days && p.price).map((p) => `${p.days}d $${p.price}`)].join(" / ")}` : `3 days $${pubPricing.three_day_package} · week $${pubPricing.weekly_special}`}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/45">Rental Coverage</label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {[
                        { id: "own", t: "Use My Own Insurance", p: "$0/day" },
                        { id: "rental", t: "Third-Party Rental Insurance", p: tpi.enabled ? `+$${tpi.price_per_day}/day` : "Currently Unavailable", off: !tpi.enabled },
                        { id: "decline", t: "Decline Rental Insurance", p: "$0/day" },
                      ].map((o) => (
                        <button key={o.id} type="button" data-testid={`wizard-coverage-${o.id}`} disabled={o.off} onClick={() => set("coverage_selection", o.id)}
                          className={`rounded-lg border p-3 text-left transition-colors ${o.off ? "border-white/10 opacity-50 cursor-not-allowed" : form.coverage_selection === o.id ? "border-[#F2B705] bg-[#F2B705]/[0.07]" : "border-white/15"}`}>
                          <div className="text-xs font-semibold">{o.t}</div>
                          <div className={`font-display font-black ${o.off ? "text-white/40 text-xs mt-1" : "text-gold-gradient text-base"}`}>{o.p}</div>
                        </button>
                      ))}
                    </div>
                    {form.coverage_selection === "decline" && <p className="text-amber-300 text-xs mt-2 leading-relaxed p-3 rounded-lg border border-amber-400/40 bg-amber-400/[0.06]"><b>{DECLINE_NOTICE}</b></p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/45">Pickup</label>
                      <input data-testid="wizard-pickup" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/45">Return</label>
                      <input data-testid="wizard-return" type="date" disabled={!!premium && !premFlexible} className={`${inputCls} [color-scheme:dark] ${premium && !premFlexible ? "opacity-60 cursor-not-allowed" : ""}`} value={form.return_date} onChange={(e) => set("return_date", e.target.value)} />
                    </div>
                  </div>
                  {premium ? (
                    <div data-testid="wizard-premium-packages" className="rounded-lg border border-[#F2B705]/50 bg-[#F2B705]/[0.08] p-3 text-xs leading-relaxed">
                      <p className="text-[#F2B705] font-bold uppercase tracking-wider mb-1.5">⭐ {premFlexible ? "Premium Vehicle — Premium Rates" : `Premium Fixed Packages${premiumOpts.length > 1 ? " — pick one" : ""}`}</p>
                      {premFlexible ? (
                        <>
                          <p className="text-white/80 font-semibold">${premium.package_price} for the first {parseInt(premium.exact_days) || 3} days · +${premium.extra_day_rate}/extra day{premiumOpts.find((o) => o.days === 7) ? ` · $${premiumOpts.find((o) => o.days === 7).price} Weekly Package (7 days)` : ""}. Regular specials don't apply.</p>
                          {days >= 3 && form.return_date && (
                            <p className="text-[#F2B705] font-bold mt-1.5" data-testid="wizard-premium-flex-base">
                              Your {days}-day base rental: ${(() => {
                                const wk = premiumOpts.find((o) => o.days === 7);
                                const bd = parseInt(premium.exact_days) || 3;
                                return (days >= 7 && wk ? wk.price + (days - 7) * premium.extra_day_rate : (premium.package_price || 0) + Math.max(0, days - bd) * premium.extra_day_rate).toLocaleString();
                              })()}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {premiumOpts.map((o) => (
                            <button key={o.days} type="button" data-testid={`wizard-prem-pkg-${o.days}`} onClick={() => setPremDays(o.days)}
                              className={`px-3 py-2 rounded-md border text-xs font-bold transition-colors ${premiumDays === o.days ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/20 text-white/70 hover:bg-white/5"}`}>
                              {o.label} — ${o.price}
                            </button>
                          ))}
                        </div>
                      )}
                      <p className="text-white/55 mt-2">{!premFlexible && `Exactly ${premiumDays} calendar days — the return date is set automatically. Regular specials don't apply. `}{premium.included_miles || 300} miles included · ${premium.deposit || 750} refundable deposit.</p>
                    </div>
                  ) : (
                    <LivePricingHint pickup={form.pickup_date} ret={form.return_date} />
                  )}
                  <p data-testid="calendar-day-note" className="text-white/40 text-xs leading-relaxed -mt-1">Rental days are counted by calendar date from pickup to return. Returning after the scheduled return time may result in additional late-return charges. Minimum rental: 3 calendar days.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/45">Pickup Time</label>
                      <input data-testid="wizard-pickup-time" type="time" className={`${inputCls} [color-scheme:dark]`} value={form.pickup_time} onChange={(e) => set("pickup_time", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/45">Return Time</label>
                      <input data-testid="wizard-return-time" type="time" className={`${inputCls} [color-scheme:dark]`} value={form.return_time} onChange={(e) => set("return_time", e.target.value)} />
                    </div>
                  </div>

                  {leadCfg.lead_enabled && earliestPickup && (
                    <div data-testid="wizard-prep-note" className={`rounded-lg border px-3 py-2.5 text-sm flex items-start gap-2 ${leadError ? "border-red-500/40 bg-red-500/[0.08] text-red-300" : "border-[#F2B705]/30 bg-[#F2B705]/[0.06] text-[#F2B705]"}`}>
                      <Clock size={15} className="mt-0.5 shrink-0" />
                      <span>{leadError ? leadError : `We prepare each vehicle before pickup — earliest available pickup is ${fmtDateTime(earliestPickup)} (${leadCfg.lead_hours}-hour prep window).`}</span>
                    </div>
                  )}

                  {selectedVehicle && rangeConflict && (
                    <div data-testid="wizard-date-conflict" className="rounded-lg border border-red-500/40 bg-red-500/[0.08] px-3 py-2.5 text-sm text-red-300 flex items-start gap-2">
                      <X size={16} className="mt-0.5 shrink-0" />
                      <span>Sorry, this car is <b>already booked {fmtDate(rangeConflict.start)} – {fmtDate(rangeConflict.end)}</b>. Please pick different dates.</span>
                    </div>
                  )}
                  {selectedVehicle && !rangeConflict && bookedRanges.length > 0 && (
                    <div data-testid="wizard-booked-dates" className="rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2.5 text-xs text-white/55">
                      <div className="text-white/45 uppercase tracking-widest mb-1">Unavailable dates for this car</div>
                      <div className="flex flex-wrap gap-1.5">
                        {bookedRanges.map((r, i) => (
                          <span key={i} className="inline-block rounded bg-white/[0.06] border border-white/10 px-2 py-0.5">{fmtDate(r.start)} – {fmtDate(r.end)}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* STEP 2: Driver */}
              {STEPS[step].id === "driver" && (
                <>
                  <input data-testid="wizard-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input data-testid="wizard-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} />
                    <input data-testid="wizard-phone" className={inputCls} placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/45">Date of Birth *</label>
                    <input data-testid="wizard-dob" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
                    {isYoung && <p className="text-[#F2B705] text-xs mt-1">Young Driver Fee (21–24): +${pubPricing.young_driver_day}/day applies.</p>}
                  </div>
                  <input data-testid="wizard-address" className={inputCls} placeholder="Home address" value={form.address} onChange={(e) => set("address", e.target.value)} />
                  <input data-testid="wizard-referral-code" className={`${inputCls} uppercase`} placeholder="Referral code (optional) — get $15 off" value={form.referral_code} onChange={(e) => set("referral_code", e.target.value.toUpperCase())} />
                </>
              )}

              {/* STEP 3: Rental Coverage Selection */}
              {STEPS[step].id === "insurance" && (
                <>
                  <div className="rounded-lg border border-white/12 p-4">
                    <div className="text-xs uppercase tracking-widest text-[#F2B705] font-bold">Rental Coverage Selection (Required)</div>
                    <div className="mt-3 space-y-2">
                      {[
                        { id: "own", t: "Option 1 — I Intend to Rely on My Own Insurance" },
                        { id: "rental", t: "Option 2 — Add Optional Third-Party Rental Insurance", off: !tpi.enabled },
                        { id: "decline", t: "Option 3 — Decline Optional Rental Insurance" },
                      ].map((o) => (
                        <label key={o.id} className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${o.off ? "border-white/10 opacity-60 cursor-not-allowed" : form.coverage_selection === o.id ? "border-[#F2B705] bg-[#F2B705]/[0.06] cursor-pointer" : "border-white/12 cursor-pointer"}`}>
                          <input type="radio" name="coverage" data-testid={`wizard-coverage-opt-${o.id}`} disabled={o.off} checked={form.coverage_selection === o.id} onChange={() => set("coverage_selection", o.id)} className="mt-1 h-4 w-4 accent-[#F2B705]" />
                          <span className="text-sm flex-1">
                            <b className="text-white">{o.t}</b>
                            {o.off && <span data-testid="wizard-tpi-unavailable" className="ml-2 text-[10px] font-bold uppercase tracking-wide text-white/45 border border-white/20 rounded px-1.5 py-0.5">Currently Unavailable</span>}
                            <span className="block text-white/60 text-xs mt-1 leading-relaxed">{o.off ? "Optional third-party rental insurance is not offered at this time. Choose Option 1 or Option 3." : COVERAGE_SHORT[o.id]}</span>
                            {!o.off && (
                              <button type="button" data-testid={`wizard-learnmore-${o.id}`} onClick={(e) => { e.preventDefault(); setLearnMore(learnMore === o.id ? null : o.id); }} className="text-[#F2B705] text-xs mt-1 hover:underline inline-flex items-center gap-1">
                                <Info size={12} /> {learnMore === o.id ? "Hide details" : "Learn more"}
                              </button>
                            )}
                            {learnMore === o.id && !o.off && <span className="block text-white/55 text-xs mt-1.5 leading-relaxed border-l-2 border-[#F2B705]/40 pl-2.5">{COVERAGE_TEXT[o.id]}</span>}
                            {o.id === "rental" && tpi.enabled && (
                              <span className="block text-white/60 text-xs mt-1.5 leading-relaxed border-l-2 border-[#F2B705]/40 pl-2.5">
                                Provider: <b className="text-white">{tpi.provider}</b> · ${tpi.price_per_day}/day{tpi.coverage_type ? ` · ${tpi.coverage_type}` : ""}
                                {tpi.coverage_limits ? <span className="block">Limits: {tpi.coverage_limits}</span> : null}
                                {tpi.exclusions ? <span className="block">Major exclusions: {tpi.exclusions}</span> : null}
                                {tpi.policy_doc_url ? <a href={tpi.policy_doc_url} target="_blank" rel="noreferrer" className="text-[#F2B705] underline">Policy documents</a> : null}
                                <span className="block mt-0.5">AutoHire of St. Louis LLC is NOT the insurance company.</span>
                              </span>
                            )}
                            {o.id === "decline" && <span className="block text-amber-300 text-xs mt-2 leading-relaxed"><b>{DECLINE_NOTICE}</b></span>}
                          </span>
                        </label>
                      ))}
                    </div>
                    {form.coverage_selection && (
                      <div data-testid="wizard-coverage-info" className={`mt-3 rounded-lg border p-3 text-xs leading-relaxed ${form.coverage_selection === "decline" ? "border-amber-400/40 bg-amber-400/[0.06] text-amber-200/90" : "border-white/12 bg-white/[0.03] text-white/65"}`}>
                        {COVERAGE_INFO[form.coverage_selection]}
                      </div>
                    )}
                    <label ref={ackRef} className={`flex items-start gap-3 cursor-pointer mt-4 rounded-lg border-2 p-3.5 transition-colors ${ackError && !form.coverage_ack ? "border-red-500 bg-red-500/[0.08] animate-pulse" : form.coverage_ack ? "border-emerald-400/40 bg-emerald-400/[0.05]" : "border-[#F2B705]/60 bg-[#F2B705]/[0.06]"}`}>
                      <input type="checkbox" data-testid="wizard-coverage-ack" checked={form.coverage_ack} onChange={(e) => { set("coverage_ack", e.target.checked); if (e.target.checked) setAckError(false); }} className="mt-0.5 h-5 w-5 accent-[#F2B705]" />
                      <span className="text-xs text-white/80"><b className="text-[#F2B705] block mb-0.5">✓ Required — please check to continue</b>I certify that I have read, understand, and voluntarily selected the rental insurance/coverage option above. I understand AutoHire is not an insurance company, does not issue insurance policies, and my financial obligations under the Rental Agreement remain enforceable regardless of whether insurance coverage exists.</span>
                    </label>
                    <div data-testid="wizard-mo-notice" className="mt-4 rounded-lg border-2 border-[#F2B705]/70 bg-[#F2B705]/[0.07] p-3.5">
                      <p className="text-[#F2B705] font-black text-xs tracking-wide">COLLISION DAMAGE WAIVER AND CAR RENTAL INSURANCE NOTICE</p>
                      <p className="text-white/80 text-[11px] mt-1.5 leading-relaxed font-semibold uppercase">{MO_NOTICE.split(": ")[1]}</p>
                      <p className="text-white/45 text-[10px] mt-1.5">You will acknowledge receipt of this notice with your initials at signing.</p>
                    </div>
                  </div>
                  {form.coverage_selection === "own" && (
                    <>
                      <p className="text-white/60 text-sm">Upload your insurance card — it must meet Auto-Hire requirements and is verified before delivery.</p>
                      <FileUpload label={form.insurance_bring_later ? "Insurance Card (optional — bringing at pickup)" : "Insurance Card *"} value={files.insurance_card} onUploaded={(p) => setFile("insurance_card", p)} testid="wizard-upload-insurance" />
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" data-testid="wizard-insurance-attest" checked={form.insurance_attested} onChange={(e) => set("insurance_attested", e.target.checked)} className="mt-1 h-4 w-4 accent-[#F2B705]" />
                        <span className="text-sm text-white/60">I confirm my policy is valid and meets Auto-Hire's coverage requirements.</span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-3">
                        <input type="checkbox" data-testid="wizard-insurance-bring-later" checked={form.insurance_bring_later} onChange={(e) => set("insurance_bring_later", e.target.checked)} className="mt-1 h-4 w-4 accent-[#F2B705]" />
                        <span className="text-sm text-white/70">I don't have my insurance on me right now. I understand I <b className="text-amber-300">must bring valid proof of insurance at pickup</b> or I won't be able to take the vehicle.</span>
                      </label>
                    </>
                  )}
                  {form.coverage_selection === "rental" && tpi.enabled && (
                    <div className="rounded-lg border border-[#F2B705]/30 bg-[#F2B705]/[0.06] p-5 text-center">
                      <ShieldCheck className="text-[#F2B705] mx-auto" size={32} />
                      <h4 className="font-display font-bold text-lg mt-2">Optional Third-Party Rental Insurance Selected</h4>
                      <p className="text-white/60 text-sm mt-1.5">{tpi.provider} — ${tpi.price_per_day}/day, added on top of your base rental. Coverage is issued by an independent third-party insurance provider and is subject to that provider's terms, limits, conditions, and exclusions. AutoHire of St. Louis LLC is NOT the insurance company.</p>
                    </div>
                  )}
                  {form.coverage_selection === "decline" && (
                    <div className="rounded-lg border-2 border-amber-400/50 bg-amber-400/[0.07] p-4">
                      <div className="text-amber-300 font-bold text-sm">Optional Rental Insurance Declined</div>
                      <p className="text-white/70 text-xs mt-1.5 leading-relaxed">The Damage Waiver is a separate, OPTIONAL $15/day selection you can accept or decline on the Extras step. {DW_LANGUAGE}</p>
                    </div>
                  )}
                </>
              )}

              {/* STEP 4: ID Verification */}
              {STEPS[step].id === "verify" && (
                <>
                  <p className="text-white/60 text-sm">Identity verification is required. Photos are stored privately and only seen by Auto-Hire.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FileUpload label="License — Front *" value={files.license_front} onUploaded={(p) => setFile("license_front", p)} testid="wizard-upload-front" />
                    <FileUpload label="License — Back *" value={files.license_back} onUploaded={(p) => setFile("license_back", p)} testid="wizard-upload-back" />
                  </div>
                  <FileUpload label="Selfie (face clearly visible) *" value={files.selfie} onUploaded={(p) => setFile("selfie", p)} testid="wizard-upload-selfie" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                    <div className="sm:col-span-1">
                      <label className="text-xs uppercase tracking-widest text-white/45">DL Number *</label>
                      <input data-testid="wizard-dl-number" className={inputCls} placeholder="License #" value={form.dl_number} onChange={(e) => set("dl_number", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/45">DL State *</label>
                      <input data-testid="wizard-dl-state" maxLength={2} className={`${inputCls} uppercase`} placeholder="MO" value={form.dl_state} onChange={(e) => set("dl_state", e.target.value.toUpperCase())} />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/45">DL Expiration *</label>
                      <input data-testid="wizard-dl-expiration" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.dl_expiration} onChange={(e) => set("dl_expiration", e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              {/* STEP 5: Add-ons */}
              {STEPS[step].id === "addons" && (
                <>
                  <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-white/12 p-4">
                    <input type="checkbox" data-testid="wizard-extra-driver" checked={form.add_extra_driver} onChange={(e) => set("add_extra_driver", e.target.checked)} className="mt-1 h-4 w-4 accent-[#F2B705]" />
                    <span className="text-sm">
                      <b>Additional Approved Driver</b> — +${pubPricing.additional_driver_day}/day
                      <span className="block text-white/50">Only listed drivers may operate the vehicle.</span>
                    </span>
                  </label>
                  {form.add_extra_driver && (
                    <div className="space-y-4 pl-1">
                      <input data-testid="wizard-extra-driver-name" className={inputCls} placeholder="Additional driver full name *" value={form.extra_driver_name} onChange={(e) => set("extra_driver_name", e.target.value)} />
                      <FileUpload label="Additional Driver's License *" value={files.extra_driver_license} onUploaded={(p) => setFile("extra_driver_license", p)} testid="wizard-upload-extra-license" />
                    </div>
                  )}
                  <div data-testid="wizard-waiver-select" className="rounded-lg border border-white/12 p-4">
                    <b className="text-sm">OPTIONAL Damage Waiver / Damage Protection — ${pubPricing.damage_waiver_day}/day</b>
                    <p className="text-[#F2B705] text-xs font-bold mt-1">THE DAMAGE WAIVER IS OPTIONAL AND IS NOT INSURANCE.</p>
                    <span className="block text-white/50 text-xs mt-1">{DW_LANGUAGE}</span>
                    <div className="mt-3 space-y-2">
                      <label className={`flex items-center gap-3 cursor-pointer rounded-md border p-2.5 ${waiverOn ? "border-[#F2B705] bg-[#F2B705]/[0.06]" : "border-white/12"}`}>
                        <input type="radio" name="dw" data-testid="wizard-damage-accept" checked={waiverOn} onChange={() => set("add_damage_protection", true)} className="h-4 w-4 accent-[#F2B705]" />
                        <span className="text-sm">I <b>ACCEPT</b> Damage Waiver — ${pubPricing.damage_waiver_day}/day</span>
                      </label>
                      <label className={`flex items-center gap-3 cursor-pointer rounded-md border p-2.5 ${!waiverOn ? "border-[#F2B705] bg-[#F2B705]/[0.06]" : "border-white/12"}`}>
                        <input type="radio" name="dw" data-testid="wizard-damage-decline" checked={!waiverOn} onChange={() => set("add_damage_protection", false)} className="h-4 w-4 accent-[#F2B705]" />
                        <span className="text-sm">I <b>DECLINE</b> Damage Waiver</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* STEP 6: Review */}
              {STEPS[step].id === "review" && (
                <div className="space-y-3 text-sm">
                  <Row label="Vehicle" value={selectedVehicle} />
                  <Row label="Coverage" value={form.coverage_selection === "rental" ? "Option 2 — Optional Third-Party Rental Insurance" : form.coverage_selection === "decline" ? "Option 3 — Declined Optional Rental Insurance" : "Option 1 — My Own Insurance"} />
                  <Row label="Damage Waiver (optional)" value={waiverOn ? `Accepted — $${pubPricing.damage_waiver_day}/day` : "Declined"} />
                  <Row label="Rental Dates" value={`${form.pickup_date} → ${form.return_date}`} />
                  <Row label="Rental Duration" value={`${days} day${days > 1 ? "s" : ""}`} />
                  <Row label="Times" value={`Pickup ${form.pickup_time || "—"} · Return ${form.return_time || "—"}`} />
                  <Row label="Driver" value={`${form.name} · ${form.phone}`} />
                  <div className="border-t border-white/10 my-2" />
                  <p className="text-white/50 text-xs uppercase tracking-widest font-semibold">Itemized Charges</p>
                  {quote ? (
                    <PricingBreakdown quote={quote} dueLabel="Total Due Today" />
                  ) : (
                    <>
                      <Row label={`Base Rental (${days >= 7 ? "Weekly Special" : "3-Day Special"}${days !== 3 && days !== 7 ? ` + $${pubPricing.additional_day_rate}/extra day` : ""})`} value={`$${vehicleBase.toFixed(2)}`} />
                      {insuranceType === "AllIn" && <Row label={`All-In Coverage ($${pubPricing.allin_coverage_day}/day × ${days})`} value={`$${(pubPricing.allin_coverage_day * days).toFixed(2)}`} />}
                      {form.add_extra_driver && <Row label={`Additional Driver ($${pubPricing.additional_driver_day}/day × ${days})`} value={`$${(pubPricing.additional_driver_day * days).toFixed(2)}`} />}
                      {waiverOn && <Row label={`Damage Waiver ($${pubPricing.damage_waiver_day}/day × ${days})`} value={`$${(pubPricing.damage_waiver_day * days).toFixed(2)}`} />}
                      {isYoung && <Row label={`Young driver fee ($${pubPricing.young_driver_day}/day × ${days})`} value={`$${(pubPricing.young_driver_day * days).toFixed(2)}`} />}
                      <div className="flex justify-between items-center bg-[#F2B705]/[0.08] border border-[#F2B705]/30 rounded-lg px-4 py-3">
                        <span className="font-semibold">Total Due Today</span>
                        <span className="font-display font-black text-2xl text-gold-gradient" data-testid="wizard-total">${total}</span>
                      </div>
                    </>
                  )}
                  <p className="text-white/40 text-xs">No standard security deposit. Final charges confirmed at approval.{quote?.tax_config?.enabled ? "" : " Taxes are not applied to this booking."}</p>
                  <p className="text-white/55 text-xs border border-white/10 rounded-md px-3 py-2 bg-white/[0.03]" data-testid="pricing-acknowledgment">
                    By continuing, you acknowledge that you have reviewed and accepted the itemized charges above.
                  </p>
                  <label className="flex items-start gap-3 cursor-pointer pt-1">
                    <input type="checkbox" data-testid="wizard-agree" checked={form.agreed} onChange={(e) => set("agreed", e.target.checked)} className="mt-1 h-4 w-4 accent-[#F2B705]" />
                    <span className="text-sm text-white/60">I agree to the rental terms, identity verification, and consent to be contacted by phone, SMS and email.</span>
                  </label>
                </div>
              )}
            </div>

            {/* Footer: running total + nav */}
            <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-white/10 px-6 py-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">Est. Total</div>
                <div className="font-display font-black text-xl text-gold-gradient">${total}</div>
              </div>
              <div className="flex items-center gap-2">
                {step > 0 && (
                  <button data-testid="wizard-back" onClick={goBack} className="flex items-center gap-1 border border-white/15 px-4 py-2.5 rounded-md font-semibold hover:bg-white/5 transition-colors">
                    <ChevronLeft size={16} /> Back
                  </button>
                )}
                {step < STEPS.length - 1 ? (
                  <button data-testid="wizard-next" onClick={handleNext} className="flex items-center gap-1 bg-[#F2B705] text-black px-5 py-2.5 rounded-md font-bold hover:bg-white transition-colors">
                    Next <ChevronRight size={16} />
                  </button>
                ) : (
                  <button data-testid="wizard-submit" onClick={submit} disabled={loading} className="flex items-center gap-2 bg-[#F2B705] text-black px-5 py-2.5 rounded-md font-bold hover:bg-white transition-colors disabled:opacity-60">
                    {loading && <Loader2 className="animate-spin" size={16} />} Submit Application
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-white/45">{label}</span>
    <span className="text-white text-right font-medium">{value || "—"}</span>
  </div>
);
